import { apiSchemas } from '@verifime/api-definition';
import type { TSelectableEntityType } from '@verifime/components';
import { z } from 'zod';
import {
  criteriaFromDeclaration,
  permittedEntityTypes,
  readDeclaredEntityType,
} from './requestCriteria';

/** `GET /v1/tracking/{trackingReference}/joinable-invites`: the opened invite first, then the rest. */
export type TJoinableInvitesResponse = z.infer<typeof apiSchemas.JoinableInvitesResponse>;

type TJoinableInvite = TJoinableInvitesResponse['invites'][number];

/**
 * What the customer's joined invites show the screens. The server decides which invites join and
 * which declaration governs them; this only reads that answer.
 */
export type TJoinedInvites = {
  /** True when the list holds an invite other than the opened one. */
  joined: boolean;
  /** The declared entity type that governs the listed invites, as the server gives it. */
  governingDeclaration: string | undefined;
  /**
   * The organisation name the partner declared for this entity type, as given: plain text, never
   * re-validated (person-api omits a name that fails its name rule). Undefined when no listed
   * invite names the type, or its name is blank.
   */
  declaredOrganisationNameFor(entityType: TSelectableEntityType): string | undefined;
};

const isBlank = (value: string | null | undefined): boolean => !value || value.trim() === '';

/** The opened invite first, whatever order the server listed them in. */
const openedFirst = (invites: TJoinableInvite[]): TJoinableInvite[] => [
  ...invites.filter((invite) => invite.opened),
  ...invites.filter((invite) => !invite.opened),
];

/**
 * Whether an invite's declaration permits the type. An invite that declares nothing, or a value
 * this portal does not know, permits no type here: a name only goes with a declaration.
 */
const declarationPermits = (
  invite: TJoinableInvite,
  entityType: TSelectableEntityType,
): boolean => {
  if (!readDeclaredEntityType(invite.declaredEntityType)) {
    return false;
  }
  return permittedEntityTypes(
    criteriaFromDeclaration(invite.declaredEntityType, 'DEFAULT'),
  ).includes(entityType);
};

export function readJoinedInvites(response: TJoinableInvitesResponse): TJoinedInvites {
  const invites = openedFirst(response.invites);

  return {
    joined: invites.some((invite) => !invite.opened),
    governingDeclaration: response.governingDeclaration ?? undefined,
    declaredOrganisationNameFor: (entityType) => {
      const naming = invites.find((invite) => declarationPermits(invite, entityType));
      const name = naming?.declaredOrganisationName;
      return isBlank(name) ? undefined : (name ?? undefined);
    },
  };
}
